import httpClientService from './httpClientService'
import { microbiomeTableFields } from '../model/microbiomeProfile'
import PHYLUMS from '../model/phylum'

const taxonomyAttributes = {
  species: 'prokaryoteProfile',
  family: 'familyProfile',
  genus: 'genusProfile',
  phylum: 'phylumProfile'
}

const taxonomyRanks = [
  { name: 'domain', abbreviation: 'd' },
  { name: 'phylum', abbreviation: 'p' },
  { name: 'class', abbreviation: 'c' },
  { name: 'order', abbreviation: 'o' },
  { name: 'family', abbreviation: 'f' },
  { name: 'genus', abbreviation: 'g' },
  { name: 'species', abbreviation: 's' }
]

export default class ReportDataService {
  static getDataFromAPI (url, type, data) {
    if (!url) {
      throw new Error('URL is missing.')
    }
    switch (type) {
      case 'get':
        return httpClientService.getReportAPIHttpClient().get(url)
      case 'put':
        return httpClientService.getReportAPIHttpClient().get(url, data)
      case 'post':
        return httpClientService.getReportAPIHttpClient().get(url, data)
      default:
        throw new Error('API CRUD type is not defined or does not exist.')
    }
  }

  static async getGalaxyChartData (primarySample = null, secondarySample = null) {
    if (!primarySample || !primarySample.identifier) throw new Error('primarySample is missing.')
    const data = {}
    await this.getDataFromAPI(`sample/${primarySample.identifier}/overview`, 'get')
      .then(response => {
        data.primarySample = {
          value: response.data.healthPercentage && response.data.healthPercentage.value ? response.data.healthPercentage.value : null
        }
      })

    if (secondarySample && secondarySample.identifier) {
      await this.getDataFromAPI(`sample/${secondarySample.identifier}/overview`, 'get')
        .then(response => {
          data.secondarySample = {
            value: response.data.healthPercentage && response.data.healthPercentage.value ? response.data.healthPercentage.value : null
          }
        })
    }
    return data
  }

  // get the diversity level data for indicator bar
  static async getDiversityLevelData (primarySample = null, secondarySample = null) {
    if (!primarySample || !primarySample.identifier) throw new Error('primarySample is missing.')
    const data = {}
    await this.getDataFromAPI(`/sample/${primarySample.identifier}/diversity`, 'get')
      .then(response => {
        data.primarySample = response.data ? response.data : null
      })

    if (secondarySample) {
      await this.getDataFromAPI(`/sample/${secondarySample.identifier}/diversity`, 'get')
        .then(response => {
          data.secondarySample = response.data ? response.data : null
        })
    }
    return data
  }

  static async getMicrobiomeProfileTableData (primarySample = null, secondarySample = null, comparisonQuery = 'healthy', type = 'species') {
    if (!primarySample) throw new Error('primarySample is missing.')
    const fields = this.getFields(secondarySample)
    let speciesData = null
    const tableData = []
    let allPrimarySpecies = null

    await this.getAllSpecies(primarySample.identifier)
      .then(response => {
        allPrimarySpecies = response.data.speciesDescription
      })
    await this.getComparisonData(type, comparisonQuery)
      .then(response => {
        speciesData = response.data[taxonomyAttributes[type]]
      })
    await this.getSampleProfileSpecies(primarySample.identifier)
      .then(response => {
        if (response.data && response.data.abundanceProfile) {
          Object.entries(response.data.abundanceProfile).forEach(speciesAbundanceProfile => {
            const [speciesKey, speciesAbundanceData] = speciesAbundanceProfile
            const taxonomyLevel = speciesAbundanceData.taxonomy ? speciesAbundanceData.taxonomy : ''
            const taxonomyLevels = taxonomyLevel.split(';')
            const abundance = speciesAbundanceData.abundance ? speciesAbundanceData.abundance : 0
            let nameKey = null
            // check if taxonomyLevels have requested type (species, family, genus, phylum) in it only then proceed compiling data
            taxonomyLevels.forEach((taxonomyLevel, taxonomyLevelIndex) => {
              const abbreviation = taxonomyLevel.trim().substring(0, 1)
              const taxonomyKey = taxonomyRanks.filter(taxonomyRank => taxonomyRank.abbreviation === abbreviation && taxonomyRank.name === type)
              if (taxonomyKey.length) {
                nameKey = taxonomyLevels[taxonomyLevelIndex].trim()
              }
            })
            // Check taxonomy levels end here

            // if (speciesData[speciesKey]) {
            const rangeLow = speciesData[speciesKey] && speciesData[speciesKey].rangeLow ? speciesData[speciesKey].rangeLow : 0
            const rangeHigh = speciesData[speciesKey] && speciesData[speciesKey].rangeHigh ? speciesData[speciesKey].rangeHigh : 0
            let level = 'Average'
            if (abundance > rangeHigh) {
              level = 'High'
            } else if (abundance < rangeLow) {
              level = 'Low'
            } else if (abundance === 0) {
              level = 'Undetected'
            }

            const itemAlreadyInList = tableData.find(data => data.taxonomyRankKey === nameKey)
            if (!itemAlreadyInList) {
              tableData.push({
                healthDiseaseAssociation: !allPrimarySpecies[speciesKey] ? '' : allPrimarySpecies[speciesKey].isHealthy ? '<i class="fa fa-plus-circle"></i>' : allPrimarySpecies[speciesKey].isDisease ? '<i class="fa fa-minus-circle"></i>' : '',
                Phylum: taxonomyLevels && taxonomyLevels[1] && PHYLUMS[taxonomyLevels[1].trim()] ? PHYLUMS[taxonomyLevels[1].trim()].name : '',
                taxonomyRankKey: nameKey,
                Species: speciesData[speciesKey] && speciesData[speciesKey].displayName ? speciesData[speciesKey].displayName : '',
                displayName: speciesData[nameKey] ? speciesData[nameKey].displayName : '',
                Abundance: abundance.toFixed(2) + '%',
                'Abundance A': abundance.toFixed(2) + '%',
                abundanceValue: abundance,
                abundanceValueSecondarySample: 0,
                Range: `${rangeLow.toFixed(2)}% - ${rangeHigh.toFixed(2)}%`,
                Level: level
              })
            } else {
              itemAlreadyInList.abundanceValue += abundance
              itemAlreadyInList.Abundance = itemAlreadyInList.abundanceValue.toFixed(2) + '%'
            }
          })
        }
      })
    if (secondarySample) {
      await this.getSampleProfileSpecies(secondarySample.identifier)
        .then(response => {
          if (response.data && response.data.abundanceProfile) {
            Object.entries(response.data.abundanceProfile).forEach(speciesAbundanceProfile => {
              const [speciesKey, speciesAbundanceData] = speciesAbundanceProfile
              const abundance = speciesAbundanceData.abundance ? speciesAbundanceData.abundance : 0
              const taxonomyLevel = speciesAbundanceData.taxonomy ? speciesAbundanceData.taxonomy : ''
              const taxonomyLevels = taxonomyLevel.split(';')
              let nameKey = null
              // check if taxonomyLevels have requested type (species, family, genus, phylum) in it only then proceed compiling data
              taxonomyLevels.forEach((taxonomyLevel, taxonomyLevelIndex) => {
                const abbreviation = taxonomyLevel.trim().substring(0, 1)
                const taxonomyKey = taxonomyRanks.filter(taxonomyRank => taxonomyRank.abbreviation === abbreviation && taxonomyRank.name === type)
                if (taxonomyKey.length) {
                  nameKey = taxonomyLevels[taxonomyLevelIndex].trim()
                }
              })
              // Check taxonomy levels end here

              // if (speciesData[speciesKey]) {
              const rangeLow = speciesData[speciesKey] && speciesData[speciesKey].rangeLow ? speciesData[speciesKey].rangeLow : 0
              const rangeHigh = speciesData[speciesKey] && speciesData[speciesKey].rangeHigh ? speciesData[speciesKey].rangeHigh : 0
              let level = 'Average'
              if (abundance > rangeHigh) {
                level = 'High'
              } else if (abundance < rangeLow) {
                level = 'Low'
              } else if (abundance === 0) {
                level = 'Undetected'
              }

              const itemAlreadyInList = tableData.find(data => data.taxonomyRankKey === nameKey)
              if (!itemAlreadyInList) {
                tableData.push({
                  healthDiseaseAssociation: !allPrimarySpecies[speciesKey] ? '' : allPrimarySpecies[speciesKey].isHealthy ? '<i class="fa fa-plus-circle"></i>' : allPrimarySpecies[speciesKey].isDisease ? '<i class="fa fa-minus-circle"></i>' : '',
                  Phylum: taxonomyLevels && taxonomyLevels[1] && PHYLUMS[taxonomyLevels[1].trim()] ? PHYLUMS[taxonomyLevels[1].trim()].name : '',
                  taxonomyRankKey: nameKey,
                  Species: speciesData[speciesKey] && speciesData[speciesKey].displayName ? speciesData[speciesKey].displayName : '',
                  displayName: speciesData[nameKey] ? speciesData[nameKey].displayName : '',
                  Abundance: '',
                  'Abundance A': '',
                  abundanceValue: 0,
                  'Abundance B': abundance.toFixed(2) + '%',
                  abundanceValueSecondarySample: abundance,
                  Range: `${rangeLow.toFixed(2)}% - ${rangeHigh.toFixed(2)}%`,
                  Level: level
                })
              } else {
                itemAlreadyInList.abundanceValueSecondarySample += abundance
                itemAlreadyInList['Abundance B'] = itemAlreadyInList.abundanceValueSecondarySample.toFixed(2) + '%'
              }
            })
          }
        })
    }
    return {
      fields: fields,
      data: tableData
    }
  }

  /* Get all sample information kind of glossary and is same for every sample */
  static async getAllSpecies (sampleIdentifier) {
    if (!sampleIdentifier) throw new Error('sampleIdentifier is missing.')
    const url = `/sample/${sampleIdentifier}/species`
    return this.getDataFromAPI(url, 'get')
  }

  /* Get all numbers or species data for a sample */
  static async getSampleProfileSpecies (sampleIdentifier) {
    if (!sampleIdentifier) throw new Error('sampleIdentifier is missing.')
    const url = `/sample/${sampleIdentifier}/profile-species`
    return this.getDataFromAPI(url, 'get')
  }

  /* Get all Eukaryotes for samples */
  static async getMicrobialEukaryotes (primarySample = null, secondarySample = null) {
    if (!primarySample || !primarySample.identifier) throw new Error('primarySample is missing.')
    const data = {}

    await this.getDataFromAPI(`/sample/${primarySample.identifier}/euk-profile`, 'get')
      .then(response => {
        data.primarySample = {
          value: response.data.eukaryoteProfile ? response.data.eukaryoteProfile : null
        }
      })

    if (secondarySample && secondarySample.identifier) {
      await this.getDataFromAPI(`sample/${secondarySample.identifier}/euk-profile`, 'get')
        .then(response => {
          data.secondarySample = {
            value: response.data.eukaryoteProfile ? response.data.eukaryoteProfile : null
          }
        })
    }
    return data
  }

  /* Get all Microbial metabolites data for samples */
  static async getMicrobialMetabolites (primarySample = null, secondarySample = null) {
    if (!primarySample || !primarySample.identifier) throw new Error('primarySample is missing.')
    const data = {}

    await this.getDataFromAPI(`/sample/${primarySample.identifier}/compound-profile`, 'get')
      .then(response => {
        data.primarySample = {
          value: response.data.compoundProfile ? response.data.compoundProfile : null
        }
      })

    if (secondarySample && secondarySample.identifier) {
      await this.getDataFromAPI(`sample/${secondarySample.identifier}/compound-profile`, 'get')
        .then(response => {
          data.secondarySample = {
            value: response.data.compoundProfile ? response.data.compoundProfile : null
          }
        })
    }
    return data
  }

  /* Get all macronutrients data for samples */
  static async getMacronutrients (primarySample = null, secondarySample = null) {
    if (!primarySample || !primarySample.identifier) throw new Error('primarySample is missing.')
    const data = {}

    await this.getDataFromAPI(`/sample/${primarySample.identifier}/macronutrient-counts`, 'get')
      .then(response => {
        data.primarySample = {
          value: response.data.macronutrientProfile ? response.data.macronutrientProfile : null
        }
      })

    if (secondarySample && secondarySample.identifier) {
      await this.getDataFromAPI(`sample/${secondarySample.identifier}/macronutrient-counts`, 'get')
        .then(response => {
          data.secondarySample = {
            value: response.data.macronutrientProfile ? response.data.macronutrientProfile : null
          }
        })
    }
    return data
  }

  static async getComparisonData (taxonomy, comparisonQuery) {
    const url = `/comparison/insight-xt/${comparisonQuery}/profile-${taxonomy}`
    return this.getDataFromAPI(url, 'get')
  }

  static async getComparisonCompoundProfile (comparisonQuery) {
    if (!comparisonQuery) throw new Error('comparisonQuery is missing.')
    const url = `/comparison/insight-xt/${comparisonQuery}/compound-profile`
    return this.getDataFromAPI(url, 'get')
  }

  // @kan.zhang -> please refactor getComparisonData2.
  // getComparisonData2 means nothing also null checks.
  static async getComparisonData2 (taxonomy, comparisonQuery) {
    // euk-profile is used for Eukaryotes
    const url = `/comparison/insight-xt/${comparisonQuery}/${taxonomy}-profile`
    return this.getDataFromAPI(url, 'get')
  }

  static getFields (secondarySample) {
    const fields = Object.keys(microbiomeTableFields).map(field => {
      return {
        key: field,
        label: microbiomeTableFields[field].label,
        sortable: microbiomeTableFields[field].sortable,
        actionable: microbiomeTableFields[field].actionable ? microbiomeTableFields[field].actionable : false
      }
    })

    if (secondarySample) {
      const abundanceIndex = fields.findIndex(field => field.key === 'Abundance')
      fields.map(field => {
        field.label = field.key === 'Abundance' ? 'Abundance A' : field.label
      })
      fields.splice(abundanceIndex + 1, 0, {
        key: 'Abundance B',
        sortable: true
      })
    }
    return fields
  }

  // Get data for mental health questionnaire tab
  static async getMentalHealthData (primarySample = null, secondarySample = null) {
    if (!primarySample || !primarySample.identifier) throw new Error('primarySample is missing.')
    const data = {}

    await this.getDataFromAPI(`/sample/${primarySample.identifier}/daas-results`, 'get')
      .then(response => {
        data.primarySample = response.data && response.data.values.length ? response.data.values : null
      })

    if (secondarySample && secondarySample.identifier) {
      await this.getDataFromAPI(`/sample/${secondarySample.identifier}/daas-results`, 'get')
        .then(response => {
          data.secondarySample = response.data && response.data.values.length ? response.data.values : null
        })
    }
    return data
  }

  // Get data for pie chart of food questionnaire
  static async getFoodData (primarySample = null, secondarySample = null) {
    if (!primarySample || !primarySample.identifier) throw new Error('primarySample is missing.')
    const data = {}
    const nutrientAdgList = ['10', '20', '30', '40', '50']

    await this.getDataFromAPI(`/sample/${primarySample.identifier}/nutrition-results`, 'get')
      .then(response => {
        data.primarySample = response && response.data ? response.data : null
        data.primaryBarChartData = response.data && response.data.servesTotal.length
          ? response.data.servesTotal.filter(nutrient => nutrientAdgList.indexOf(nutrient.adg_code) !== -1) : null
      })

    if (secondarySample && secondarySample.identifier) {
      await this.getDataFromAPI(`/sample/${secondarySample.identifier}/nutrition-results`, 'get')
        .then(response => {
          data.secondarySample = response && response.data ? response.data : null
          data.secondaryBarChartData = response.data && response.data.servesTotal.length
            ? response.data.servesTotal.filter(nutrient => nutrientAdgList.indexOf(nutrient.adg_code) !== -1) : null
        })
    }
    return data
  }
}



//////////////////
// WEBPACK FOOTER
// ./~/@microbadevs/report/src/services/reportDataService.js
// module id = RkqP
// module chunks = 0